import { Accordion, AccordionHeader, SvgEdit, Tooltip } from '@ui-kit'
import React, { useEffect } from 'react'
import {
  doesThisRowFailValidation,
  getElementFromAddedFields,
} from '../utils/form.utils'
import ElementRenderer from './ElementRenderer'
import {
  RowRendererInnerProps,
  RowRendererProps,
} from '../types/formRenderer.types'
import { FieldValues, Path, useWatch } from 'react-hook-form'
import { ViewRenderer } from './FormRenderer'
import {
  componentNames,
  MODE_CONSTANTS,
  NAMESPACES,
  PERMISSION_MODULES,
  ROW_TYPES,
} from '../utils/constants'
import { RootState, useAppSelector } from '../utils/agent.store'
import {
  hasSensitiveDataPermission,
  permissionObject,
} from '../utils/helperFunctions'
import { LayoutRowType } from '@app-store'
import { isEmpty } from 'lodash'
import { useLayoutRenderTabsContext } from './tabs/LayoutRenderTabsContext'
import {
  getEffectiveHidden,
  isRowHiddenByFormBehavior,
} from '../form-behavior/formBehavior.utils'
import { getFormBehaviorRuleKey } from '../form-behavior/useFormBehaviorFieldOverrides'
import { useFormBehaviorState } from '../form-behavior/useFormBehaviorState'

const RowRenderer = <T extends FieldValues>({
  row,
  errors,
  mode,
  idx,
  clearErrors,
  control,
  setValue,
  register,
  layout,
  data,
  entityId,
  hideAccordion = false,
  formUniqueId = '',
  reregisterKey,
  fullLayout,
  tabIndexMap,
  dataId,
  subFormMetaObject,
  isStickySummary,
  fromIntakeReview,
  showPatientInformation,
  getValues,
  task,
  layoutRowType,
}: RowRendererProps<T>) => {
  const RowValidationFailed = doesThisRowFailValidation(row, errors)
  const allEntities = useAppSelector(
    (state: RootState) => state.entity.entitiesByName
  )
  const globalPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.globalPermissions
  )
  const { handleEdit } = useLayoutRenderTabsContext()
  const formBehavior = useFormBehaviorState(fullLayout?.uid)

  if (isRowHiddenByFormBehavior(row?.rowKey, formBehavior.hiddenOverrides)) {
    return null
  }

  const rowClassName = () => {
    if (RowValidationFailed) {
      return '!border-l-error-500'
    } else if (isStickySummary) {
      return 'bg-secondary-25'
    }
  }
  const isEntityGlobal = (row?: LayoutRowType) => {
    const entityKey = fullLayout?.entity_name
    const entity = allEntities[entityKey ?? '']
    if (isEmpty(row?.container_behavior)) {
      return entity?.source_type === NAMESPACES.GLOBAL_KEY
    } else {
      return (
        row?.container_behavior?.toUpperCase() ===
        NAMESPACES?.GLOBAL_KEY?.toUpperCase()
      )
    }
  }

  const hasGlobalPermission = () => {
    if (fullLayout?.entity_name === 'plans' && mode === 'add') return true
    return (
      (isEntityGlobal(row) &&
        permissionObject.Manage_Global_Schema(
          globalPermissions,
          PERMISSION_MODULES.MANAGE_GLOBAL_DATA
        )) ||
      !isEntityGlobal(row)
    )
  }
  return hideAccordion ? (
    <div>
      {row.label ? (
        <div className="text-base/[1.375rem] font-medium pb-3">{row.label}</div>
      ) : null}
      <RowRenderInner
        row={row}
        errors={errors}
        mode={mode}
        idx={idx}
        clearErrors={clearErrors}
        control={control}
        setValue={setValue}
        register={register}
        layout={layout}
        data={data}
        entityId={entityId}
        formUniqueId={formUniqueId}
        reregisterKey={reregisterKey}
        hideAccordion={hideAccordion}
        fullLayout={fullLayout}
        dataId={dataId}
        subFormMetaObject={subFormMetaObject}
        fromIntakeReview={fromIntakeReview}
        showPatientInformation={showPatientInformation}
        getValues={getValues}
        task={task}
      />
    </div>
  ) : (
    <Accordion
      heading={
        !isStickySummary ? (
          <AccordionHeader>
            <div className="flex items-center gap-3">
              <span className="">{row.label || NAMESPACES.INFORMATION}</span>
              {(() => {
                const isGlobal =
                  isEntityGlobal(row) && !row?.meta?.disable_global_client

                const isClient =
                  row?.container_behavior?.toLowerCase() ===
                    NAMESPACES?.CLIENT?.toLowerCase() &&
                  !row?.meta?.disable_global_client

                if (isGlobal) {
                  return (
                    <span className="flex items-center bg-secondary-50 border-secondary-25 border h-5 rounded-sm px-2 py-1 text-accent-600 text-xxs">
                      {NAMESPACES.GLOBAL}
                    </span>
                  )
                }

                if (isClient && !isEmpty(row?.container_behavior)) {
                  return (
                    <span className="flex items-center bg-progress-25 border-taskStatus-300 border h-5 rounded-sm px-2 py-1 text-accent-600 text-xxs">
                      {NAMESPACES.CLIENT}
                    </span>
                  )
                }

                return null
              })()}
              {layoutRowType === ROW_TYPES.SUB_FORM && (
                <div className="ml-auto" onClick={() => handleEdit?.()}>
                  <SvgEdit />
                </div>
              )}
            </div>
          </AccordionHeader>
        ) : (
          <AccordionHeader>
            <div className="text-secondary-500 mr-2">{row?.label}</div>
          </AccordionHeader>
        )
      }
      className={rowClassName()}
      triggerOpen={RowValidationFailed}
      isDefaultActive={
        layoutRowType === ROW_TYPES.SUB_FORM
          ? false
          : [0, 1, 2].includes(idx || 0)
      }
      id={row.rowId}
      alwaysOpen={isStickySummary}
    >
      <RowRenderInner
        row={row}
        errors={errors}
        mode={hasGlobalPermission() ? mode : 'view'}
        idx={idx}
        clearErrors={clearErrors}
        control={control}
        setValue={setValue}
        register={register}
        layout={layout}
        data={data}
        entityId={entityId}
        formUniqueId={formUniqueId}
        hideAccordion={hideAccordion}
        reregisterKey={reregisterKey}
        fullLayout={fullLayout}
        tabIndexMap={tabIndexMap}
        dataId={dataId}
        subFormMetaObject={subFormMetaObject}
        fromIntakeReview={fromIntakeReview}
        showPatientInformation={showPatientInformation}
        getValues={getValues}
        task={task}
      />
    </Accordion>
  )
}

const RowRenderInner = <T extends FieldValues>({
  row,
  errors,
  mode,
  clearErrors,
  control,
  setValue,
  register,
  layout,
  data,
  entityId,
  formUniqueId = '',
  hideAccordion = false,
  reregisterKey,
  fullLayout,
  dataId,
  subFormMetaObject,
  fromIntakeReview,
  getValues,
  showPatientInformation,
  task,
}: RowRendererInnerProps<T>) => {
  const pateintDetails = useAppSelector(
    (state) => state?.patientData?.patientData
  )
  const subscriberRelation = useWatch({
    name: 'subscriber_relationship_to_patient',
  })
  const layoutUid = fullLayout?.uid
  const formBehavior = useFormBehaviorState(layoutUid)

  useEffect(() => {
    if (showPatientInformation && subscriberRelation?.id === 105) {
      setValue(
        'subscriber_first_name' as Path<T>,
        pateintDetails?.first_name ?? ''
      )
      setValue(
        'subscriber_last_name' as Path<T>,
        pateintDetails?.last_name ?? ''
      )
      setValue('subscriber_dob' as Path<T>, pateintDetails?.date_of_birth ?? '')
    }
  }, [subscriberRelation])

  const columns = row?.columns ?? []
  const maxLength = Math.max(
    ...columns.map((col) => col.elements?.length ?? 0),
    0
  )
  const columnCount = columns.length || 1
  const cellStyle = {
    flex: `0 0 calc((100% - ${(columnCount - 1) * 1.25}rem) / ${columnCount})`,
    maxWidth: `calc((100% - ${(columnCount - 1) * 1.25}rem) / ${columnCount})`,
  }

  return (
    <div
      className={`row flex flex-wrap gap-5 w-full ${
        hideAccordion ? '' : 'p-4'
      }`}
    >
      {Array.from({ length: maxLength }).map((_, rowIndex) =>
        columns.map((col) => {
          const el = col.elements?.[rowIndex]
          if (!el) {
            return (
              <div
                key={`${col.columnId}-${rowIndex}`}
                className="invisible min-w-[11.69rem]"
                style={cellStyle}
                aria-hidden
              />
            )
          }
          const element = getElementFromAddedFields(
            el,
            entityId,
            layout?.added_fields
          )
          const ruleKey = getFormBehaviorRuleKey(element)
          if (
            getEffectiveHidden(
              ruleKey,
              el.is_hidden,
              formBehavior.hiddenOverrides
            )
          ) {
            return null
          }
          const elementWithoutTabIndex = { ...element }
          delete elementWithoutTabIndex.tabIndex
          const isSensitiveField =
            el?.keyName === 'is_client_controlled' &&
            !hasSensitiveDataPermission()
          return (
            <div
              key={`${col.columnId}-${rowIndex}`}
              className="min-w-[11.69rem]"
              style={cellStyle}
            >
              {mode === MODE_CONSTANTS.VIEW &&
                element?.elementName !== componentNames.FILE_UPLOAD && (
                  <ViewRenderer
                    key={el.id}
                    element={element}
                    control={control}
                    layoutId={fullLayout?.id}
                    layout={fullLayout}
                    task={task}
                  />
                )}
              <div
                className={
                  mode === MODE_CONSTANTS.VIEW &&
                  element?.elementName !== componentNames.FILE_UPLOAD
                    ? 'hidden'
                    : ''
                }
              >
                <Tooltip content={element.meta.tooltip_text ?? ''}>
                  <React.Fragment key={reregisterKey}>
                    <div className="hidden keyName">{element.keyName}</div>
                    <div className="hidden elementName">
                      {element.elementName}
                    </div>
                    <ElementRenderer
                      dataId={dataId}
                      data={data ?? []}
                      key={el.id}
                      element={{
                        ...elementWithoutTabIndex,
                        meta: {
                          ...element.meta,
                          default_value:
                            mode === MODE_CONSTANTS.ADD
                              ? element.meta.default_value
                              : '',
                          defaultValue:
                            mode === MODE_CONSTANTS.ADD
                              ? element.meta.default_value
                              : '',
                          disabled: isSensitiveField || element?.isDisabled,
                        },
                      }}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      control={control}
                      clearErrors={clearErrors}
                      mode={mode}
                      formUniqueId={formUniqueId}
                      fullLayout={fullLayout}
                      subFormMetaObject={subFormMetaObject}
                      fromIntakeReview={fromIntakeReview}
                      getValues={getValues}
                    />
                  </React.Fragment>
                </Tooltip>
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

export default RowRenderer
